import { createEffect, sample } from 'effector';
import { t } from 'i18n';
import { get, isEmpty } from 'lodash';
import { $answers } from 'models/answers';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import { $isTrustFundingPage } from 'models/location';
import $$trustFunding from 'models/pages/trust-funding';
import { $qInstances } from 'models/q-instances';
import navigate from 'models/router-gate';
import { $global } from 'models/store-global';
import { z } from 'zod';

import TrustFundingService from 'services/TrustFundingService';
import { createTunnel } from 'utils/factories/create-tunnel';

import { type Question } from 'types/zod/question';

// If EP is complete and user has answered that they want to create a living trust or a trust for two people, then set the page to be available.
sample({
    source: {
        isComplete: $$ep.stores.$epQInstance.map((epQInstance) => epQInstance?.status === 'Complete'),
        createLivingTrust: $$ep.stores.$epAnswers.map(
            (answers) => !!get(answers, 'enhanced_estate_feature.avoid_probate.create_living_trust'),
        ),
        oneOrTwoTrusts: $$ep.stores.$epAnswers.map(
            (answers) => !!get(answers, 'enhanced_estate_feature.avoid_probate.one_or_two_trusts'),
        ),
        trustFundingMenuItem: createTunnel($global, (_state) => t('features.trustFundingMenuItem')),
        isUpdating: $$ep.stores.$epAnswers.map((answers) => !isEmpty(answers?.update)),
        generatedOnce: $$auth.$currentParticipant.map((p) => p?.generated_once),
        TFAnswersExists: $$trustFunding.$answers.map((answers) => !isEmpty(answers)),
    },
    fn: ({
        isComplete,
        createLivingTrust,
        oneOrTwoTrusts,
        trustFundingMenuItem,
        isUpdating,
        TFAnswersExists,
        generatedOnce,
    }) =>
        !!trustFundingMenuItem &&
        (((isComplete || isUpdating) && (createLivingTrust || oneOrTwoTrusts) && generatedOnce) || TFAnswersExists),
    target: $$trustFunding.$pageIsAvailable,
});

// When user clicks the "Get Started" button, take the epQInstanceId and participantId and create a trust funding questionnaire instance.
sample({
    source: {
        epQInstanceId: $$ep.stores.$epQInstanceId,
        participantId: $$ep.stores.$epQInstance.map((epQInstance) => epQInstance?.originator_participant_id),
    },
    clock: $$trustFunding.createQInstanceButtonClicked,
    filter: ({ epQInstanceId, participantId }) => !!epQInstanceId && !!participantId,
    fn: ({ epQInstanceId, participantId }) => ({
        parent_questionnaire_instance_id: z.number().parse(epQInstanceId),
        questionnaire_type_code: 'TRUST_FUND' as const,
        originator_participant_id: z.number().parse(participantId),
    }),
    target: $$trustFunding.createTrustFundingFx,
});

// When the trust funding questionnaire instance is created, navigate to the first page of the questionnaire.
sample({
    clock: $$trustFunding.createTrustFundingFx.doneData,
    fn: (result) =>
        `/document/${result.questionnaire_type_code}/${result.state_code}/${result.questionnaire_instance_id}`,
    target: createEffect((path: string) => navigate(path)),
});

// When trust funding page is opened, load TF questionnaire.
sample({
    source: {
        state: $$auth.$currentParticipant.map((p) => p?.domicile_state_code),
        isTrustFundingPage: $isTrustFundingPage,
    },
    filter: ({ state, isTrustFundingPage }) => !!state && isTrustFundingPage,
    fn: ({ state }) => ({ state: z.string().parse(state), questionnaireType: 'TRUST_FUND' as const }),
    target: $$api.questionnaires.getQuestionnaireFx,
});

// When the TF questionnaire is loaded, get the trust funding categories and store them in $$trustFunding.$categories.
sample({
    source: $$trustFunding.$questionnaire,
    clock: $$ep.stores.$epQuestionnaire,
    filter: (questionnaire): questionnaire is Question => !!questionnaire,
    fn: TrustFundingService.getCategories,
    target: $$trustFunding.$categories,
});

// When get TF questionnaires request succeeds, find trust funding questionnaire and store it in $$trustFunding.$questionnaire.
sample({
    source: {
        questionnaires: $global.map((state) => state.questionnaires),
        state: $$auth.$currentParticipant.map((p) => p?.address?.domicile?.state_code),
    },
    filter: ({ questionnaires, state }) => !!questionnaires && !!state,
    fn: ({ questionnaires, state }) => (state && questionnaires?.[state]?.TRUST_FUND) || null,
    target: $$trustFunding.$questionnaire,
});

// When get TF qInstances request succeeds, find trust funding questionnaire instance and store it in $$trustFunding.$qInstance.
sample({
    clock: $qInstances.map(
        (qInstances) =>
            Object.values(qInstances).find((qInstance) => qInstance.questionnaire_type_code === 'TRUST_FUND') || null,
    ),
    target: $$trustFunding.$qInstance,
});

// When TF qInstnace is loaded, get qInstance id and request answers.
sample({
    clock: $$trustFunding.$qInstance.map((qInstance) => qInstance?.questionnaire_instance_id),
    filter: Boolean,
    fn: (qInstanceId) => ({ id: qInstanceId }),
    target: $$api.answers.getAnswersFx,
});

// When get TF answers request succeeds, store answers in $$trustFunding.$answers.
sample({
    source: {
        tfQInstanceId: $$trustFunding.$qInstance.map((qInstance) => qInstance?.questionnaire_instance_id),
        answers: $answers,
    },
    fn: ({ tfQInstanceId, answers }) => (tfQInstanceId && answers?.[tfQInstanceId]) || null,
    target: $$trustFunding.$answers,
});

// When TF answers loaded, find assets and store them in $$trustFunding.$assets.
sample({
    clock: $$trustFunding.$answers,
    fn: TrustFundingService.getAssets,
    target: $$trustFunding.$assets,
});

// If TF qInstance exists, then set the $isAlreadyCreated to true.
sample({
    source: $$trustFunding.$qInstance,
    fn: Boolean,
    target: $$trustFunding.$isAlreadyCreated,
});

// When Print instructions button clicked.
sample({
    source: $$trustFunding.$qInstance,
    clock: $$trustFunding.printInstructionsButtonClicked,
    filter: Boolean,
    fn: (qInstance) => {
        return {
            questionnaireInstanceId: qInstance.questionnaire_instance_id,
        };
    },
    target: $$api.documents.generateFx,
});

// When Add asset button clicked, take create empty asset and jump to the asset page.
sample({
    source: $$trustFunding.$qInstance,
    clock: $$trustFunding.addAssetClicked,
    filter: Boolean,
    fn: (qInstance) => {
        const assetPath = TrustFundingService.getAssetQuestionPath('new');
        const path = `/document/${qInstance.questionnaire_type_code}/${qInstance.state_code}/${qInstance.questionnaire_instance_id}?jumpTo=${assetPath}`;
        return path;
    },
    target: createEffect((path: string) => navigate(path)),
});

// When done checkbox clicked, update assets in answers by id and save answers.
sample({
    source: {
        id: $$trustFunding.$qInstance.map((qInstance) => qInstance?.questionnaire_instance_id),
        answers: $$trustFunding.$answers,
    },
    clock: $$trustFunding.doneAssetClicked,
    filter: ({ id }) => !!id,
    fn: ({ id, answers }, assetId) => ({
        id: z.number().parse(id),
        answers: TrustFundingService.toggleDone(answers, assetId),
    }),
    target: $$api.answers.putAnswersFx,
});

// When delete asset button clicked, update assets in answers by id and save answers.
sample({
    source: {
        id: $$trustFunding.$qInstance.map((qInstance) => qInstance?.questionnaire_instance_id),
        answers: $$trustFunding.$answers,
    },
    clock: $$trustFunding.deleteAssetClicked,
    filter: ({ id }) => !!id,
    fn: ({ id, answers }, assetId) => ({
        id: z.number().parse(id),
        answers: TrustFundingService.deleteAsset(answers, assetId),
    }),
    target: $$api.answers.putAnswersFx,
});

// When asset is clicked, get asset path in questionnaire and jump to it.
sample({
    source: $$trustFunding.$qInstance,
    clock: $$trustFunding.assetClicked,
    filter: Boolean,
    fn: (qInstance, assetId) => {
        const assetPath = TrustFundingService.getAssetQuestionPath(assetId);
        const path = `/document/${qInstance.questionnaire_type_code}/${qInstance.state_code}/${qInstance.questionnaire_instance_id}?jumpTo=${assetPath}`;
        return path;
    },
    target: createEffect((path: string) => navigate(path)),
});
